/**
 * Customized version of react-select.
 *
 * TODO: This is a first attempt to align the component UI with the design team's desired specs:
 * https://www.figma.com/file/Z6fObCiWnXEVBTtxCpKT4r/Specs?node-id=110%3A904&t=QmO1vN7DlR5knw3Z-0
 * It doesn't match the spec completely yet; waiting on UI framework decisions before investing
 * more time in alignment.
 */

import {Colors, hexToRGB} from '@wandb/common/css/globals.styles';
import React from 'react';
import ReactSelect, {
  components,
  DropdownIndicatorProps,
  GroupBase,
  Props,
  StylesConfig,
} from 'react-select';

import {Icon} from '../Icon';

export const SelectSizes = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const;
export type SelectSize = typeof SelectSizes[keyof typeof SelectSizes];

const HEIGHTS: Record<SelectSize, number> = {
  small: 24,
  medium: 32,
  large: 40,
} as const;

const LINE_HEIGHTS: Record<SelectSize, string> = {
  small: '20px',
  medium: '24px',
  large: '24px',
} as const;

const FONT_SIZES: Record<SelectSize, string> = {
  small: '14px',
  medium: '16px',
  large: '16px',
} as const;

const PADDING: Record<SelectSize, string> = {
  small: '2px 8px',
  medium: '4px 12px',
  large: '8px 12px',
} as const;

interface AdditionalProps {
  size?: SelectSize;
}

// See: https://react-select.com/typescript
export const Select = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> & AdditionalProps
) => {
  // Toggle icon when open
  const DropdownIndicator = (
    indicatorProps: DropdownIndicatorProps<Option, IsMulti, Group>
  ) => {
    const iconName = indicatorProps.selectProps.menuIsOpen
      ? 'chevron-up'
      : 'chevron-down';
    return (
      <components.DropdownIndicator {...indicatorProps}>
        <Icon name={iconName} width={16} height={16} color={Colors.GRAY_500} />
      </components.DropdownIndicator>
    );
  };

  // Override styling to come closer to design spec.
  // See: https://react-select.com/home#custom-styles
  // See: https://github.com/JedWatson/react-select/issues/2728
  const size = props.size ?? 'medium';
  const styles: StylesConfig<Option, IsMulti, Group> = {
    // No vertical line to left of dropdown indicator
    indicatorSeparator: baseStyles => ({...baseStyles, display: 'none'}),
    input: baseStyles => {
      return {
        ...baseStyles,
        padding: 0,
        margin: 0,
      };
    },
    valueContainer: baseStyles => {
      const padding = PADDING[size];
      return {...baseStyles, padding};
    },
    dropdownIndicator: baseStyles => ({...baseStyles, padding: '0 8px'}),
    container: baseStyles => {
      const height = HEIGHTS[size];
      return {
        ...baseStyles,
        height,
      };
    },
    control: (baseStyles, state) => {
      const colorBorderDefault = Colors.GRAY_350;
      const colorBorderHover = hexToRGB(Colors.TEAL_LIGHT, 0.4);
      const colorBorderOpen = hexToRGB(Colors.TEAL_LIGHT, 0.64);
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      return {
        ...baseStyles,
        height,
        minHeight: height,
        lineHeight,
        fontSize,
        border: 0,
        boxShadow: state.menuIsOpen
          ? `0 0 0 2px ${colorBorderOpen}`
          : state.isFocused
          ? `0 0 0 2px ${colorBorderHover}`
          : `inset 0 0 0 1px ${colorBorderDefault}`,
        '&:hover': {
          boxShadow: state.menuIsOpen
            ? `0 0 0 2px ${colorBorderOpen}`
            : `0 0 0 2px ${colorBorderHover}`,
        },
      };
    },
    groupHeading: (baseStyles, state) => {
      return {
        ...baseStyles,
        fontSize: '16px',
        fontWeight: 600,
        color: Colors.GRAY_800,
        textTransform: 'none',
      };
    },
    option: (baseStyles, state) => {
      return {
        ...baseStyles,
        cursor: state.isDisabled ? 'default' : 'pointer',
        // TODO: Should icon be translucent?
        color: state.isDisabled ? Colors.GRAY_400 : Colors.GRAY_800,
        height: '36px',
        padding: '6px 12px',
        backgroundColor: state.isDisabled
          ? undefined
          : state.isSelected
          ? Colors.GRAY_100
          : state.isFocused
          ? Colors.GRAY_50
          : undefined,
        ':active': {
          // mousedown
          ...baseStyles[':active'],
          backgroundColor: !state.isDisabled ? Colors.GRAY_100 : undefined,
        },
      };
    },
  };

  return (
    <ReactSelect {...props} components={{DropdownIndicator}} styles={styles} />
  );
};
